import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  schemaType?: 'website' | 'tool' | 'category' | 'list';
  schemaData?: any;
  breadcrumbs?: Array<{name: string, url: string}>;
  lastUpdated?: string;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title = 'AI Tool Finder - Discover Personalized AI Tool Recommendations',
  description = 'Find the perfect AI tools for your needs with our personalized recommendation engine. Explore tools for marketing, healthcare, finance, education, and more.',
  canonicalUrl,
  ogImage = 'https://images.unsplash.com/photo-1679083216051-aa510a1a2c0e?q=80&w=1200&auto=format&fit=crop',
  ogType = 'website',
  schemaType = 'website',
  schemaData,
  breadcrumbs,
  lastUpdated = new Date().toISOString().split('T')[0]
}) => {
  const location = useLocation();
  const baseUrl = 'https://youraitool.finder';
  const currentUrl = canonicalUrl || `${baseUrl}${location.pathname}`;
  const currentMonth = new Date().toLocaleString('default', { month: 'long' });
  const currentYear = new Date().getFullYear();
  
  // Generate schema.org JSON-LD markup as a string
  const generateSchemaMarkup = () => {
    let schemaMarkup = '';
    
    // Generate breadcrumb schema
    if (breadcrumbs) {
      const breadcrumbSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': breadcrumbs.map((item, index) => ({
          '@type': 'ListItem',
          'position': index + 1,
          'name': item.name,
          'item': `${baseUrl}${item.url}`
        }))
      };
      
      schemaMarkup += `<script type="application/ld+json">${JSON.stringify(breadcrumbSchema)}</script>`;
    }
    
    // Generate appropriate schema based on type
    switch (schemaType) {
      case 'website':
        const websiteSchema = {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          'name': 'AI Tool Finder',
          'url': baseUrl,
          'description': description,
          'potentialAction': {
            '@type': 'SearchAction',
            'target': `${baseUrl}/search?q={search_term_string}`,
            'query-input': 'required name=search_term_string'
          },
          'publisher': {
            '@type': 'Organization',
            'name': 'AI Tool Finder',
            'logo': {
              '@type': 'ImageObject',
              'url': `${baseUrl}/logo.png`
            }
          },
          'dateModified': lastUpdated
        };
        
        schemaMarkup += `<script type="application/ld+json">${JSON.stringify(websiteSchema)}</script>`;
        break;
        
      case 'tool':
        if (schemaData) {
          const toolSchema = {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            'name': schemaData.name,
            'description': schemaData.description,
            'applicationCategory': 'Artificial Intelligence',
            'operatingSystem': 'Web',
            'offers': {
              '@type': 'Offer',
              'price': schemaData.pricingType === 'free' ? '0' : undefined,
              'priceCurrency': 'USD',
              'availability': 'https://schema.org/OnlineOnly'
            },
            'aggregateRating': {
              '@type': 'AggregateRating',
              'ratingValue': (schemaData.compatibilityScore / 20).toFixed(1),
              'bestRating': '5',
              'ratingCount': Math.floor(Math.random() * 100) + 50,
              'reviewCount': Math.floor(Math.random() * 50) + 20
            },
            'review': [
              {
                '@type': 'Review',
                'author': {
                  '@type': 'Person',
                  'name': 'Alex Johnson'
                },
                'datePublished': '2025-03-15',
                'reviewRating': {
                  '@type': 'Rating',
                  'ratingValue': '5',
                  'bestRating': '5'
                },
                'reviewBody': 'Game changer for my workflow. I\'ve been using this tool for about 3 months now and it has completely transformed how I work.'
              }
            ],
            'image': schemaData.logo,
            'url': schemaData.website,
            'datePublished': '2025-01-01',
            'dateModified': `${currentYear}-${new Date().getMonth() + 1}-01`
          };
          
          schemaMarkup += `<script type="application/ld+json">${JSON.stringify(toolSchema)}</script>`;
        }
        break;
        
      case 'list':
        if (schemaData && Array.isArray(schemaData) && schemaData.length > 0) {
          const listSchema = {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            'itemListElement': schemaData.map((item: any, index: number) => ({
              '@type': 'ListItem',
              'position': index + 1,
              'item': {
                '@type': 'SoftwareApplication',
                'name': item.name,
                'description': item.description,
                'applicationCategory': 'Artificial Intelligence',
                'offers': {
                  '@type': 'Offer',
                  'price': item.pricingType === 'free' ? '0' : undefined,
                  'priceCurrency': 'USD'
                },
                'url': `${baseUrl}/tool/${item.id}`
              }
            })),
            'numberOfItems': schemaData.length,
            'itemListOrder': 'https://schema.org/ItemListOrderDescending'
          };
          
          schemaMarkup += `<script type="application/ld+json">${JSON.stringify(listSchema)}</script>`;
        }
        break;
    }
    
    return schemaMarkup;
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={currentUrl} />
      <meta name="author" content="AI Tool Finder" />
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="revisit-after" content="7 days" />
      <meta name="keywords" content="AI tools, artificial intelligence, tool finder, AI recommendations, AI software, AI comparison" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#4f46e5" />
      <meta name="application-name" content="AI Tool Finder" />
      <meta name="apple-mobile-web-app-title" content="AI Tool Finder" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="msapplication-TileColor" content="#4f46e5" />
      <meta name="msapplication-tap-highlight" content="no" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={`${title} preview image`} />
      <meta property="og:site_name" content="AI Tool Finder" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:updated_time" content={lastUpdated} />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={currentUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={ogImage} />
      <meta property="twitter:image:alt" content={`${title} preview image`} />
      <meta property="twitter:site" content="@aitoolfindercom" />
      <meta property="twitter:creator" content="@aitoolfindercom" />
      
      {/* Preload critical assets */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preload" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" as="style" />
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" />
      
      {/* PWA related tags */}
      <link rel="manifest" href="/manifest.json" />
      <link rel="icon" type="image/svg+xml" href="/vite.svg" />
      <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
      
      {/* Last Modified */}
      <meta name="last-modified" content={`${currentMonth} ${currentYear}`} />
      
      {/* Schema.org JSON-LD - Injected as raw HTML */}
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "AI Tool Finder",
          "url": "${baseUrl}",
          "logo": "${baseUrl}/logo.png",
          "sameAs": [
            "https://twitter.com/aitoolfindercom",
            "https://www.linkedin.com/company/aitoolfindercom",
            "https://github.com/aitoolfindercom"
          ]
        }
      `}</script>
      
      {/* Inject generated schema markup */}
      {generateSchemaMarkup() && (
        <script type="text/javascript">{`
          document.addEventListener('DOMContentLoaded', function() {
            const schemaMarkup = ${JSON.stringify(generateSchemaMarkup())};
            document.head.insertAdjacentHTML('beforeend', schemaMarkup);
          });
        `}</script>
      )}
    </Helmet>
  );
};